import { LOAD_FAILED_EVENT, MOUNT_READY_EVENT, MOUNT_READY_MARKER } from "#ce/framework/mount-ready";

const observations = new WeakMap();

/** Shares mutation and readiness observation for connections in the same DOM tree. */
export function observeConnections(root, listener, attributes = []) {
  let observation = observations.get(root);

  if (!observation) {
    const listeners = new Set();
    const attributeNames = new Set([MOUNT_READY_MARKER, "data-sp-ce-failed"]);
    let queued = false;

    function notify() {
      queued = false;

      for (const callback of [...listeners]) {
        callback();
      }
    }

    function schedule() {
      if (!queued) {
        queued = true;
        queueMicrotask(notify);
      }
    }

    const observer = new MutationObserver(schedule);

    root.addEventListener(MOUNT_READY_EVENT, schedule, true);
    root.addEventListener(LOAD_FAILED_EVENT, schedule, true);
    observation = {
      listeners,
      attributeNames,
      observer,
      schedule,
    };
    observations.set(root, observation);
  }

  for (const attribute of attributes) {
    observation.attributeNames.add(attribute);
  }

  observation.observer.observe(root, {
    childList: true,
    subtree: true,
    attributes: true,
    attributeFilter: [...observation.attributeNames],
  });
  observation.listeners.add(listener);

  return function stop() {
    observation.listeners.delete(listener);

    if (observation.listeners.size === 0) {
      observation.observer.disconnect();
      root.removeEventListener(MOUNT_READY_EVENT, observation.schedule, true);
      root.removeEventListener(LOAD_FAILED_EVENT, observation.schedule, true);
      observations.delete(root);
    }
  };
}

/** Observes a host's containing trees and releases obsolete roots after a move. */
export function observeHostConnections(host, listener, attributes = []) {
  const stops = new Map();
  let closed = false;

  function reconcile() {
    if (closed) {
      return;
    }

    const roots = new Set([host.ownerDocument]);
    let root = host.getRootNode();

    while (root && (root.nodeType === Node.DOCUMENT_NODE || root.host)) {
      roots.add(root);
      root = root.host?.getRootNode();
    }

    for (const [tree, stop] of stops) {
      if (!roots.has(tree)) {
        stop();
        stops.delete(tree);
      }
    }

    for (const tree of roots) {
      if (!stops.has(tree)) {
        stops.set(tree, observeConnections(tree, reconcile, attributes));
      }
    }

    listener();
  }

  reconcile();

  return function stop() {
    closed = true;

    for (const disconnect of stops.values()) {
      disconnect();
    }

    stops.clear();
  };
}

/** Reconciles a ready element connection and retires it when its target changes. */
export function createElementConnection({ resolve, connect, changed = () => {} }) {
  let target;
  let connection;
  let disposed = false;

  function reconcile() {
    if (disposed) {
      return;
    }

    const { element, error } = resolve();
    const ready = element?.hasAttribute(MOUNT_READY_MARKER);

    if (target !== element || !ready || connection?.closed) {
      connection?.disconnect();
      connection = undefined;
      target = element;
    }

    changed({
      element,
      error,
      ready: Boolean(ready),
    });

    if (ready && !connection) {
      connection = connect(element);
    }
  }

  function disconnect() {
    disposed = true;
    connection?.disconnect();
    connection = undefined;
  }

  return {
    reconcile,
    disconnect,
  };
}
